import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getStonadConfig } from '../../config/stonadConfigs';
import { authFetch } from '../../utils/auth';
import Card from '../../components/common/Card';
import { PlusIcon } from '../../components/common/icons';
import AddFamilyMemberModal from '../../components/account/AddFamilyMemberModal';
import { childKey, draftSubject, useSoknadChildren } from './soknadChildren';
import RestartDialog from './RestartDialog';

const READY_STATUSES = ['generated', 'downloaded'];

const STATES = {
  notStarted: { label: 'Ikke startet', className: 'text-text-muted' },
  inProgress: { label: 'Under arbeid', className: 'text-primary-700' },
  ready: { label: 'Klar for nedlasting', className: 'text-sage-700' },
};

const stateOf = (draft) => {
  if (!draft) return 'notStarted';
  return READY_STATUSES.includes(draft.status) ? 'ready' : 'inProgress';
};

// When a søknad under arbeid was last changed: "endret for 3 min siden".
const changedAgo = (updatedAt) => {
  const minutes = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 60000);
  if (Number.isNaN(minutes)) return null;
  if (minutes < 1) return 'endret akkurat nå';
  if (minutes < 60) return `endret for ${minutes} min siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `endret for ${hours} ${hours === 1 ? 'time' : 'timer'} siden`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'endret i går';
  if (days < 7) return `endret for ${days} dager siden`;
  return `endret ${new Date(updatedAt).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })}`;
};

// The app's primary button (as "La oss begynne"); full width under the text
// on phones, right-aligned from 640px.
const primaryButton = `
  w-full sm:w-auto px-8 py-3 rounded-xl
  bg-primary text-white font-semibold
  hover:bg-primary-600 hover:shadow-primary
  transition-all duration-200
  disabled:opacity-50 disabled:cursor-not-allowed
`;
// Quiet text links for the secondary actions; 44px tall on phones.
const textLink = `
  inline-flex items-center min-h-[44px] sm:min-h-0 sm:py-1
  text-sm text-text-secondary underline decoration-neutral-300 underline-offset-4
  hover:text-text-primary hover:decoration-current
`;

/**
 * SoknadOverview — /soknad/:type. One row per child: the state of that
 * child's søknad and what to do next. Every way in without a draft id (the
 * chat CTA, "Start hjelpestønad-søknad") lands here, so the parent always
 * picks the child.
 *
 * Visiting never creates a draft. "Start søknad" creates one for that child
 * (POST is find-or-create on the child) and opens it by id.
 *
 * "Legg til barn", the last row, saves a child to the family list (the
 * Konto → Min familie modal, child relations only). The child is listed at
 * once as "Ikke startet" and briefly highlighted; no søknad is started.
 */
export default function SoknadOverview() {
  const { type } = useParams();
  const navigate = useNavigate();
  const config = getStonadConfig(type);
  const {
    children, loading: childrenLoading, error: childrenError, retry: retryChildren, childAdded,
  } = useSoknadChildren();

  // The user's drafts, grouped by child (childKey), most recently updated first.
  const [draftsByChild, setDraftsByChild] = useState(null);
  const [loadError, setLoadError] = useState(false);
  // The child whose draft is being created. Every "Start søknad" is disabled
  // meanwhile, so a double tap sends one create.
  const [startingKey, setStartingKey] = useState(null);
  const [startErrorKey, setStartErrorKey] = useState(null);
  const [restartRow, setRestartRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // The row of the child just added; highlighted until 2.5 s after the modal
  // closes.
  const [highlightKey, setHighlightKey] = useState(null);
  const highlightRef = useRef(null);

  const loadDrafts = useCallback(async () => {
    setLoadError(false);
    setDraftsByChild(null);
    try {
      const response = await authFetch(`/soknad/${type}/drafts`);
      const drafts = await response.json();
      const grouped = new Map();
      [...drafts]
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .forEach((draft) => {
          const key = childKey(draft);
          grouped.set(key, [...(grouped.get(key) || []), draft]);
        });
      grouped.forEach((childDrafts) => {
        if (childDrafts.length > 1) {
          console.warn(
            `[SoknadOverview] ${childDrafts.length} ${type} drafts for one child ` +
            `(${childDrafts.map((draft) => draft._id).join(', ')}); showing the most recently updated`
          );
        }
      });
      setDraftsByChild(grouped);
    } catch (err) {
      console.error('[SoknadOverview] Load error:', err);
      setLoadError(true);
    }
  }, [type]);

  const isKnownType = Boolean(config);
  useEffect(() => {
    if (isKnownType) loadDrafts();
  }, [isKnownType, loadDrafts]);

  // A row per child with that child's draft. A draft for someone who is no
  // longer in the family list keeps a row, so no søknad goes missing.
  const rows = useMemo(() => {
    if (!draftsByChild) return [];
    const result = children.map((child) => ({
      ...child,
      draft: draftsByChild.get(child.key)?.[0] || null,
    }));
    draftsByChild.forEach(([draft], key) => {
      if (!children.some((child) => child.key === key)) {
        result.push({
          key,
          name: draft.subjectName || 'Barnet ditt',
          subject: draftSubject(draft),
          draft,
        });
      }
    });
    return result;
  }, [children, draftsByChild]);

  const handleChildAdded = (entityId, name) => {
    childAdded(entityId, name);
    setHighlightKey(childKey({ subjectIsOnboardedChild: false, subjectEntityId: entityId, subjectName: name }));
  };

  useEffect(() => {
    if (!highlightKey || addOpen) return undefined;
    highlightRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    const timer = setTimeout(() => setHighlightKey(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightKey, addOpen]);

  const handleStart = async (row) => {
    setStartingKey(row.key);
    setStartErrorKey(null);

    try {
      const response = await authFetch(`/soknad/${type}/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(row.subject),
      });
      const draft = await response.json();
      navigate(`/soknad/${type}/${draft._id}`);
    } catch (err) {
      console.error('[SoknadOverview] Start error:', err);
      setStartingKey(null);
      setStartErrorKey(row.key);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-semibold text-text-primary mb-4">
            Ukjent søknadstype
          </h1>
          <p className="text-text-secondary mb-6">
            Vi fant ikke søknadstypen "{type}".
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors"
          >
            Tilbake til forsiden
          </button>
        </div>
      </div>
    );
  }

  const loading = childrenLoading || (!draftsByChild && !loadError);
  // A failed family list would leave only the onboarded child: an error, not
  // a shorter list.
  const showError = loadError || childrenError;
  const retryLoad = () => {
    if (loadError) loadDrafts();
    if (childrenError) retryChildren();
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-12">
        <h1 className="text-2xl sm:text-3xl font-semibold text-text-primary">
          Søknadshjelper
        </h1>
        <p className="text-text-secondary mt-1">
          {config.displayName} — vi hjelper deg steg for steg
        </p>
        <p className="text-text-primary mt-6">
          Velg barnet søknaden gjelder.
        </p>
        <p className="text-sm text-text-muted mt-1">
          Coord hjelper foreløpig med søknader for barn.
        </p>

        {showError ? (
          <Card variant="white" className="mt-4 text-center">
            <p className="text-text-secondary mb-4">
              Vi fikk ikke hentet søknadene dine akkurat nå.
            </p>
            <button type="button" onClick={retryLoad} className={primaryButton}>
              Prøv igjen
            </button>
          </Card>
        ) : loading ? (
          <div className="mt-4 flex items-center gap-3 text-text-muted">
            <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
            <span>Henter søknadene dine …</span>
          </div>
        ) : (
          <Card as="ul" variant="white" padding="none" className="mt-4 overflow-hidden divide-y divide-border-light">
            {rows.map((row) => {
              const state = stateOf(row.draft);
              const changed = state === 'inProgress' && changedAgo(row.draft.updatedAt);
              const forChild = <span className="sr-only"> for {row.name}</span>;
              const openWizard = () => navigate(`/soknad/${type}/${row.draft._id}`);

              return (
                <li
                  key={row.key}
                  ref={row.key === highlightKey ? highlightRef : undefined}
                  className={`p-4 sm:px-6 sm:py-5 transition-colors duration-700 ${
                    row.key === highlightKey ? 'bg-sage-100' : ''
                  }`}
                >
                  <div className="sm:flex sm:items-center sm:gap-6">
                    <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                      <span
                        aria-hidden="true"
                        className="w-10 h-10 rounded-full bg-sage-50 text-sage-700 font-semibold flex items-center justify-center flex-shrink-0"
                      >
                        {row.name.charAt(0).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <h2 className="font-semibold text-text-primary truncate">{row.name}</h2>
                        <p className="text-sm text-text-secondary mt-0.5">
                          <span className={STATES[state].className}>{STATES[state].label}</span>
                          {changed && ` · ${changed}`}
                        </p>
                        {row.draft && (
                          <div className="flex flex-wrap gap-x-5">
                            {state === 'ready' && (
                              <button type="button" onClick={openWizard} className={textLink}>
                                Rediger svar{forChild}
                              </button>
                            )}
                            <button type="button" onClick={() => setRestartRow(row)} className={textLink}>
                              Start på nytt{forChild}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 sm:mt-0 sm:flex-shrink-0">
                      {state === 'notStarted' && (
                        <button
                          type="button"
                          onClick={() => handleStart(row)}
                          disabled={startingKey !== null}
                          className={primaryButton}
                        >
                          {startingKey === row.key ? 'Starter …' : 'Start søknad'}{forChild}
                        </button>
                      )}
                      {state === 'inProgress' && (
                        <button type="button" onClick={openWizard} className={primaryButton}>
                          Fortsett{forChild}
                        </button>
                      )}
                      {state === 'ready' && (
                        <button
                          type="button"
                          onClick={() => navigate(`/soknad/${type}/${row.draft._id}/preview`)}
                          className={primaryButton}
                        >
                          Se søknaden{forChild}
                        </button>
                      )}
                    </div>
                  </div>

                  {startErrorKey === row.key && (
                    <p role="alert" className="mt-3 text-sm text-error-600">
                      Det gikk ikke å starte søknaden. Prøv igjen.
                    </p>
                  )}
                </li>
              );
            })}
            <li>
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="w-full flex items-center gap-3 sm:gap-4 p-4 sm:px-6 text-left text-text-secondary hover:text-text-primary hover:bg-background-warm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                <span
                  aria-hidden="true"
                  className="w-10 h-10 rounded-full border border-dashed border-neutral-300 flex items-center justify-center flex-shrink-0"
                >
                  <PlusIcon className="w-5 h-5" />
                </span>
                <span className="font-medium">Legg til barn</span>
              </button>
            </li>
          </Card>
        )}
      </div>

      {/* Outside the loading branch, like Konto → Min familie, so nothing can
          unmount it mid-save. */}
      <AddFamilyMemberModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={handleChildAdded}
        childrenOnly
        existingNames={rows.map((row) => row.name)}
      />

      {restartRow && (
        <RestartDialog
          type={type}
          draftId={restartRow.draft._id}
          subject={restartRow.subject}
          childName={restartRow.name}
          hasGeneratedText={restartRow.draft.hasGeneratedContent}
          onCancel={() => setRestartRow(null)}
          onRestarted={(newDraftId) => navigate(`/soknad/${type}/${newDraftId}`)}
        />
      )}
    </div>
  );
}
